import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslations } from "use-intl";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { ReviewItem } from "@/types/review";
import BookkeepingOptionCard from "./BookkeepingOptionCard";
import ContextInput from "./ContextInput";
import { DeclineContextModal } from "./DeclineContextModal";
import { ApprovalContextModal } from "./ApprovalContextModal";
import { ReviewApiClient } from "@/clients/reviewApiClient";
import { toast } from "sonner";
import { isApprovalWithContextEnabled } from "@/config/features";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/components/ui/resizable";
import DocumentViewerPanel from "@/components/shared/DocumentViewerPanel";
import ManualBookkeepingPanel from "./ManualBookkeepingPanel";
import { LinkedSuggestionPanel } from "./LinkedSuggestionPanel";
import { MatchAlternativeList } from "./MatchAlternativeList";
import SekEquivalent from "@/components/shared/SekEquivalent";
import { useFormatters } from "@financialharmony/ui";
import { useReviewLabels } from "./useReviewLabels";
import { useMatchLabels } from "./useMatchLabels";
import { userFacingErrorMessage } from '@/utils/userFacingError';

const MAX_RETRY_ATTEMPTS = 5; // Should match backend constant

interface ReviewDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: ReviewItem | null;
  formatCurrency: (amount: number, currency: string) => string;
  onReviewComplete: () => void;
}

function reviewTypeBadgeVariant(reviewType: string): "default" | "secondary" | "outline" {
  if (reviewType === "choice") return "default";
  return reviewType === "context" ? "secondary" : "outline";
}

function choiceKind(isCompleted: boolean, reviewType: string): "approved" | "approve" | "choose" {
  if (isCompleted) return "approved";
  return reviewType === "approve" ? "approve" : "choose";
}

const ReviewDetailsModal: React.FC<ReviewDetailsModalProps> = ({
  open,
  onOpenChange,
  item,
  formatCurrency,
  onReviewComplete,
}) => {
  const t = useTranslations("consultantReview.details");
  const labels = useReviewLabels();
  const matchWords = useMatchLabels(item?.matchLabels);
  const tMatch = useTranslations("consultantReview.match");
  const { formatDate } = useFormatters();
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [pendingApprovalOptionId, setPendingApprovalOptionId] = useState<string | null>(null);

  if (!item) return null;

  const isBookkeepingSuggestion = !!item.suggestionId;
  const retryAttempt = item.retryAttempt || 0;
  const isAtMaxRetries = retryAttempt >= MAX_RETRY_ATTEMPTS;
  const isCompleted = item.status === 'completed';

  const renderAmount = () => {
    if (item.amount == null || !item.currency) return null;
    return (
      <div>
        <h4 className="text-sm font-semibold">{t("labels.amount")}</h4>
        <p className="font-medium">{formatCurrency(item.amount, item.currency)}</p>
        <SekEquivalent
          sekAmount={item.sekAmount}
          currency={item.currency}
          exchangeRate={item.exchangeRate}
          exchangeRateDate={item.exchangeRateDate}
        />
      </div>
    );
  };

  const handleOptionSelect = async (optionId: string) => {
    // Check if approval with context is enabled
    if (isApprovalWithContextEnabled() && isBookkeepingSuggestion && !isCompleted) {
      // Open approval context modal instead of submitting immediately
      setPendingApprovalOptionId(optionId);
      setShowApprovalModal(true);
      return;
    }

    // Original behavior: submit immediately
    setSelectedOptionId(optionId);
    setIsSubmitting(true);

    try {
      const result = await ReviewApiClient.submitChoice(item.id, optionId);
      if (result.success) {
        toast.success(t("toasts.completed"));
        onReviewComplete();
        setTimeout(() => {
          onOpenChange(false);
          setSelectedOptionId(null);
        }, 500);
      }
    } catch (error) {
      console.error("Error submitting choice:", error);
      const errorMessage = userFacingErrorMessage(error, t("toasts.submitFailed"));
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContextSubmit = async (contextText: string) => {
    setIsSubmitting(true);

    try {
      const result = await ReviewApiClient.submitContext(item.id, contextText);
      if (result.success) {
        toast.success(t("toasts.contextSubmitted"));
        onReviewComplete();
        setTimeout(() => {
          onOpenChange(false);
        }, 500);
      }
    } catch (error) {
      console.error("Error submitting context:", error);
      const errorMessage = userFacingErrorMessage(error, t("toasts.contextFailed"));
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApproveWithContextClick = () => {
    // The approval modal needs an option: the consultant picks one first.
    if (selectedOptionId) {
      setPendingApprovalOptionId(selectedOptionId);
      setShowApprovalModal(true);
    } else {
      toast.error(t("selectOptionFirst"));
    }
  };

  const renderChoiceContent = (bookkeepingOptions: NonNullable<ReviewItem["bookkeepingOptions"]>) => {
    const kind = choiceKind(isCompleted, item.reviewType);
    return (
      <div className="space-y-4">
        <div className="mb-4">
          <h3 className="text-lg font-semibold mb-2">{t(`heading.${kind}`)}</h3>
          <p className="text-sm text-muted-foreground">{t(`intro.${kind}`)}</p>
        </div>

        {bookkeepingOptions.length === 0 ? (
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center gap-2 text-yellow-700 text-sm font-medium mb-1">
              <AlertTriangle className="h-4 w-4" />
              {t("generationFailedTitle")}
            </div>
            <p className="text-sm text-yellow-700">{t("generationFailedBody")}</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {bookkeepingOptions.map((option) => (
              <BookkeepingOptionCard
                key={option.id}
                option={option}
                onSelect={isCompleted ? undefined : handleOptionSelect}
                isSelected={isCompleted ? option.id === item.selectedSuggestionId : selectedOptionId === option.id}
                disabled={isCompleted}
              />
            ))}
          </div>
        )}

        {/* Action buttons for bookkeeping suggestions - only show for pending items */}
        {isBookkeepingSuggestion && !isCompleted && (
          <div className="pt-4 border-t space-y-2">
            {isApprovalWithContextEnabled() && (
              <Button
                variant="default"
                onClick={handleApproveWithContextClick}
                disabled={isSubmitting || !selectedOptionId}
                className="w-full"
              >
                {t("approveWithContext")}
              </Button>
            )}
            <Button
              variant="destructive"
              onClick={() => setShowDeclineModal(true)}
              disabled={isSubmitting}
              className="w-full"
            >
              {isAtMaxRetries ? t("markForManualReview") : t("declineRetry")}
            </Button>
          </div>
        )}
      </div>
    );
  };

  const renderReviewTypeContent = () => {
    // FIN-769 D10: the system ended this suggestion when its source changed; it is never actionable.
    if (item.status === 'replaced') {
      return (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t("replacedTitle")}</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>{t("replacedBody")}</p>
            {item.replacedBySuggestionId ? (
              <Link
                to={`/review?suggestion=${encodeURIComponent(item.replacedBySuggestionId)}`}
                className="font-medium text-primary underline-offset-4 hover:underline"
              >
                {t("openCurrentSuggestion")}
              </Link>
            ) : (
              <p>{t("newSuggestionPreparing")}</p>
            )}
          </AlertDescription>
        </Alert>
      );
    }

    // FIN-769: a tax row this feature cannot book yet (Kronofogden, FIN-1145) is held here,
    // visibly, and never entered by hand.
    if (item.reviewType === 'manual_entry' && item.type === 'tax_account_transaction') {
      return (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t("caseNotHandledTitle")}</AlertTitle>
          <AlertDescription>{t("caseNotHandledBody")}</AlertDescription>
        </Alert>
      );
    }

    if (item.reviewType === 'manual_entry' && item.suggestionId) {
      return (
        <ManualBookkeepingPanel
          suggestionId={item.suggestionId}
          isCompleted={isCompleted}
          onComplete={() => {
            onReviewComplete();
            setTimeout(() => onOpenChange(false), 500);
          }}
        />
      );
    }

    if (item.reviewType === 'context') {
      if (isCompleted) {
        return (
          <div className="space-y-4">
            <div className="p-4 bg-muted rounded-lg">
              <h4 className="text-sm font-semibold mb-2">{t("completedTitle")}</h4>
              <p className="text-sm text-muted-foreground">{t("completedBody")}</p>
            </div>
          </div>
        );
      }
      return (
        <ContextInput
          question={item.contextQuestion || t("defaultQuestion")}
          details={item.contextDetails}
          onSubmit={handleContextSubmit}
          isSubmitting={isSubmitting}
        />
      );
    }

    if ((item.reviewType === 'choice' || item.reviewType === 'approve') && item.bookkeepingOptions) {
      return renderChoiceContent(item.bookkeepingOptions);
    }

    return null;
  };

  const closeAfterDecision = (message: string) => {
    toast.success(message);
    onReviewComplete();
    onOpenChange(false);
  };

  // FIN-1071: a matcher's item is decided through the one endpoint every matcher shares.
  const renderContent = () => {
    if (item.link && item.suggestionId) {
      return (
        <LinkedSuggestionPanel
          suggestionId={item.suggestionId}
          link={item.link}
          labels={matchWords}
          formatCurrency={formatCurrency}
          onRejected={() => closeAfterDecision(tMatch("toasts.rejected", { item: matchWords.item }))}
        />
      );
    }
    if (item.matching && item.suggestionId && item.bookkeepingOptions?.length && !isCompleted) {
      return (
        <MatchAlternativeList
          suggestionId={item.suggestionId}
          options={item.bookkeepingOptions}
          labels={matchWords}
          explanation={item.matching.explanation}
          formatCurrency={formatCurrency}
          onDecided={({ outcome }) => closeAfterDecision(outcome === "linked" ? tMatch("toasts.linked") : tMatch("toasts.booked"))}
        />
      );
    }
    return renderReviewTypeContent();
  };

  // The item's details and its review content: the same whether or not there is a document beside them.
  const detailsPane = (
    <>
      {/* Retry Info Banner */}
      {isBookkeepingSuggestion && retryAttempt > 0 && item.parentSuggestionId && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t("retryTitle", { attempt: String(retryAttempt), max: String(MAX_RETRY_ATTEMPTS) })}</AlertTitle>
          <AlertDescription>{t("retryBody")}</AlertDescription>
        </Alert>
      )}

      {/* Item Details */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted rounded-lg">
        <div>
          <h4 className="text-sm font-semibold">{t("labels.type")}</h4>
          <Badge variant="outline">{labels.itemType(item.type)}</Badge>
        </div>

        <div>
          <h4 className="text-sm font-semibold">{t("labels.reviewType")}</h4>
          <Badge variant={reviewTypeBadgeVariant(item.reviewType)}>{labels.reviewType(item.reviewType)}</Badge>
        </div>

        {renderAmount()}

        {item.dueDate && (
          <div>
            <h4 className="text-sm font-semibold">{t("labels.dueDate")}</h4>
            <p>{formatDate(item.dueDate, "date")}</p>
          </div>
        )}
      </div>

      {/* Vendor/Customer */}
      {(item.vendor || item.customer) && (
        <div>
          <h4 className="text-sm font-semibold">
            {item.type === 'payable' && item.vendor ? t("labels.vendor") : t("labels.customer")}
          </h4>
          <p>{item.vendor || item.customer}</p>
        </div>
      )}

      {/* Description */}
      {item.description && (
        <div>
          <h4 className="text-sm font-semibold">{t("labels.description")}</h4>
          <p className="text-sm text-muted-foreground">{item.description}</p>
        </div>
      )}

      {/* Review Type Specific Content */}
      <div className="border-t pt-6">
        {renderContent()}
      </div>
    </>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={item.filePath
        ? "max-w-6xl h-[90vh] flex flex-col"
        : "max-w-4xl max-h-[90vh] overflow-y-auto"}>
        <DialogHeader>
          <DialogTitle className="text-xl">{item.title}</DialogTitle>
          <DialogDescription>{t("reviewItem", { id: item.id })}</DialogDescription>
          {item.actionSource === 'system' && item.status !== 'replaced' && !item.link && !item.matching && (
            <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
              {t("autoClosing")}
            </span>
          )}
          {item.closingForSuggestionId && (
            <p className="text-sm text-muted-foreground">
              {t("closes", { id: item.closingForSuggestionId.substring(0, 8) })}
            </p>
          )}
          {item.journalEntryId && !item.link && (
            <Link
              to={`/bookkeeping/journal?entry=${encodeURIComponent(item.journalEntryId)}`}
              className="text-sm font-medium text-primary underline-offset-4 hover:underline"
            >
              {t("journalEntry")}
            </Link>
          )}
        </DialogHeader>

        {item.filePath ? (
          <ResizablePanelGroup direction="horizontal" className="flex-1 mt-4">
            <ResizablePanel defaultSize={45} minSize={25}>
              <DocumentViewerPanel filePath={item.filePath} fileName={item.fileName} />
            </ResizablePanel>
            <ResizableHandle withHandle />
            <ResizablePanel defaultSize={55} minSize={30} className="pl-4">
              <div className="h-full overflow-y-auto space-y-6 pr-2 pb-4">{detailsPane}</div>
            </ResizablePanel>
          </ResizablePanelGroup>
        ) : (
          <div className="space-y-6 mt-4">{detailsPane}</div>
        )}
      </DialogContent>

      {/* Decline Context Modal */}
      {isBookkeepingSuggestion && item.suggestionId && (
        <DeclineContextModal
          open={showDeclineModal}
          onOpenChange={setShowDeclineModal}
          suggestionId={item.suggestionId}
          retryAttempt={retryAttempt}
          onDeclineComplete={() => {
            onReviewComplete();
            onOpenChange(false);
          }}
        />
      )}

      {/* Approval Context Modal */}
      {isBookkeepingSuggestion && item.suggestionId && pendingApprovalOptionId && (
        <ApprovalContextModal
          open={showApprovalModal}
          onOpenChange={setShowApprovalModal}
          suggestionId={item.suggestionId}
          selectedOptionId={pendingApprovalOptionId}
          onApprovalComplete={() => {
            onReviewComplete();
            onOpenChange(false);
            setPendingApprovalOptionId(null);
          }}
        />
      )}
    </Dialog>
  );
};

export default ReviewDetailsModal;
